/**
 * IAPPaywall — Paywall iOS con prezzi App Store reali via RevenueCat.
 * ═════════════════════════════════════════════════════════════════════════
 * Compliance Apple Guideline 3.1.1:
 *   • Tutti gli acquisti su iPhone passano dal foglio nativo Apple (StoreKit).
 *   • Apple prende 15% (Small Business Program) o 30% (standard).
 *   • Bottone "Ripristina acquisti" OBBLIGATORIO e sempre visibile.
 *
 * NON viene renderizzato su web/Android: lì resta il flow Stripe attuale.
 *
 * iter v30 r145.
 */
/* eslint-disable */
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, RotateCcw, Shield, Loader2, Settings2, Gift, Bug } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  iapConfigure,
  iapGetOfferings,
  iapPurchase,
  iapRestorePurchases,
  iapSyncToBackend,
  iapShowManageSubscription,
} from "@/lib/iap";
import { useAuth } from "@/lib/auth";
import { nativeHaptic } from "@/lib/native";
import { api, BACKEND_URL } from "@/lib/api";

/** Feature highlight per piano. Allineato col sito prontoimmo.it/pricing. */
const PLAN_FEATURES = {
  base: [
    "5 immobili attivi",
    "150 crediti smart/mese (~30 staging)",
    "Annuncio · post · reel · story · carosello",
    "Sopralluogo vocale · Assistente Pronto",
    "Match clienti",
    "Pubblicazione su 4 portali",
    "Logo e colori della tua agenzia",
  ],
  pro: [
    "Immobili senza limite",
    "320 crediti smart/mese (~64 staging)",
    "Niente watermark sui contenuti",
    "Assistente Pronto illimitato (fuori crediti)",
    "Pianta arredata 2D",
    "Tutto il piano Start",
    "Supporto prioritario via email",
  ],
};

/** Da subscriptionPeriod ISO8601 → label italiana. */
function periodLabel(period) {
  if (!period) return "";
  if (period === "P1M") return "al mese";
  if (period === "P1Y") return "all'anno";
  if (period === "P3M") return "ogni 3 mesi";
  if (period === "P6M") return "ogni 6 mesi";
  if (period === "P1W") return "alla settimana";
  return "";
}

/** Da productIdentifier → plan_key. Usata internamente (helper). */
// eslint-disable-next-line no-unused-vars
function planKeyFromProductId(productId) {
  if (!productId) return null;
  if (productId.includes("start")) return "base";
  if (productId.includes("pro"))   return "pro";
  return null;
}

// iter v30 r147 — Fallback statici per UX istantanea: la prima chiamata a
// RevenueCat → Apple StoreKit può impiegare 3-8s. Per non mostrare uno spinner
// vuoto, partiamo con questi prezzi statici (cards visibili IMMEDIATAMENTE) e
// li sostituiamo coi prezzi reali Apple non appena arrivano. I bottoni di
// acquisto restano disabled finché non abbiamo i package veri RevenueCat
// (un acquisto non può partire da un fallback).
const STATIC_FALLBACK_PACKAGES = [
  {
    identifier: "$rc_monthly",
    productIdentifier: "pronto_start_monthly",
    priceString: "€29,99",
    price: 29.99,
    currencyCode: "EUR",
    title: "Pronto Start mensile",
    description: "150 crediti, 5 immobili, voce illimitata",
    subscriptionPeriod: "P1M",
    isFallback: true,
  },
  {
    identifier: "start_yearly",
    productIdentifier: "pronto_start_yearly",
    priceString: "€249,99",
    price: 249.99,
    currencyCode: "EUR",
    title: "Pronto Start annuale",
    description: "150 crediti/mese · 5 immobili · 3 mesi in regalo",
    subscriptionPeriod: "P1Y",
    isFallback: true,
  },
  {
    identifier: "pro_monthly",
    productIdentifier: "pronto_pro_monthly",
    priceString: "€49,99",
    price: 49.99,
    currencyCode: "EUR",
    title: "Pronto Pro mensile",
    description: "320 crediti/mese, immobili illimitati",
    subscriptionPeriod: "P1M",
    isFallback: true,
  },
  {
    identifier: "$rc_annual",
    productIdentifier: "pronto_pro_yearly",
    priceString: "€399,99",
    price: 399.99,
    currencyCode: "EUR",
    title: "Pronto Pro annuale",
    description: "320 crediti/mese, immobili illimitati, anno",
    subscriptionPeriod: "P1Y",
    isFallback: true,
  },
];

/** Pannello diagnostico paywall iOS. Mostrato in tutti gli stati del paywall
 *  (promo, empty, normale) quando l'utente lo apre con 5 tap sul titolo
 *  oppure aggiungendo `?dbg=1` all'URL. NIENTE info sensibili (no chiave RC,
 *  no JWT, no token), solo presenza/assenza e messaggi di errore RC/Apple. */
function PaywallDebugPanel({ show, user, diag }) {
  if (!show) return null;
  return (
    <div
      className="mt-6 rounded-lg border border-amber-400 bg-amber-50 p-3 text-[11px] text-neutral-800 space-y-1.5 font-mono text-left"
      data-testid="iap-debug-panel"
    >
      <div className="flex items-center gap-1.5 text-amber-700 font-semibold mb-1">
        <Bug className="h-3.5 w-3.5" /> Debug paywall
      </div>
      <div>email: <strong>{user?.email || "(non loggato)"}</strong></div>
      <div>plan: <strong>{user?.plan || "?"}</strong></div>
      <div className="break-all">backend: <strong>{BACKEND_URL || "(vuoto)"}</strong></div>
      <div>RC key presente: <strong>{diag.rcKeyPresent ? "sì" : "NO"}</strong></div>
      <div>app/info ok: <strong>{diag.appInfo ? "sì" : "no"}</strong>{diag.appInfoError ? ` · err: ${diag.appInfoError}` : ""}</div>
      <div>in_free_promo: <strong>{String(diag.appInfo?.in_free_promo ?? "?")}</strong></div>
      <div>RC configure: <strong>{diag.configureOk === null ? "skip" : String(diag.configureOk)}</strong>{diag.configureError ? ` · err: ${diag.configureError}` : ""}</div>
      <div>offerings ricevute: <strong>{diag.offeringsCount}</strong></div>
      {diag.offeringsIds.length > 0 && (
        <div className="break-all">ids: {diag.offeringsIds.join(", ")}</div>
      )}
      {diag.offeringsPrices && diag.offeringsPrices.length > 0 && (
        <div className="break-all">prezzi: {diag.offeringsPrices.join(" · ")}</div>
      )}
      {diag.offeringsError && (
        <div className="text-red-700">offerings err: {diag.offeringsError}</div>
      )}
      <div>elapsed: <strong>{diag.elapsedMs || 0} ms</strong></div>
      <div className="pt-1 text-amber-700/80">Manda screenshot di questo pannello per il debug.</div>
    </div>
  );
}

export default function IAPPaywall({ onPurchased }) {
  const { user } = useAuth();
  // iter v30 r147 — Mostra i prezzi statici IMMEDIATAMENTE (no spinner vuoto).
  // I prezzi reali Apple sostituiscono i fallback non appena arrivano. Loading
  // resta true solo per i bottoni di acquisto (disabled finché i package
  // veri RevenueCat non sono caricati).
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState(STATIC_FALLBACK_PACKAGES);
  const [purchasingId, setPurchasingId] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);
  // iter v30 r145 — durante FREE_PROMO la piattaforma è gratis, paywall in
  // modalità "informativa": mostra la data fine promo invece dei prezzi.
  const [promoInfo, setPromoInfo] = useState({ inPromo: false, end: null });
  // iter v31 r1 — Toggle Mensile/Annuale GLOBALE (applica a entrambi Start e Pro).
  // Allineato col sito prontoimmo.it/pricing.
  // Default annuale: mostra lo sconto e spinge l'utente alla scelta consigliata.
  const [billingPeriod, setBillingPeriod] = useState("yearly");

  // iter v31 r2 — Diagnostic state. Su iOS quando RevenueCat non risponde
  // l'utente vede solo "Sincronizzazione…" senza capire cosa succede.
  // Salviamo qui ogni step della fetch per mostrarlo nel pannello debug.
  const [diag, setDiag] = useState({
    appInfo: null,
    appInfoError: null,
    configureOk: null,
    configureError: null,
    offeringsError: null,
    offeringsCount: 0,
    offeringsIds: [],
    offeringsPrices: [],
    rcKeyPresent: !!(process.env.REACT_APP_REVENUECAT_API_KEY_IOS),
    elapsedMs: 0,
  });
  // Tap 5 volte sul titolo per mostrare/nascondere il pannello debug.
  // Oppure ?dbg=1 in URL lo apre subito.
  const [showDebug, setShowDebug] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("dbg") === "1"; }
    catch { return false; }
  });
  const [titleTaps, setTitleTaps] = useState(0);
  const onTitleTap = () => {
    const next = titleTaps + 1;
    setTitleTaps(next);
    if (next >= 5) { setShowDebug((v) => !v); setTitleTaps(0); }
    setTimeout(() => setTitleTaps(0), 1500);
  };

  const loadOfferings = useCallback(async () => {
    setLoading(true);
    setError(null);
    // iter v31 r5 — Aggiorniamo diag step-by-step così se uno step si pianta
    // il pannello debug mostra esattamente dov'è (non più valori iniziali).
    const startTs = Date.now();
    const baseDiag = {
      appInfo: null,
      appInfoError: null,
      configureOk: null,
      configureError: null,
      offeringsError: null,
      offeringsCount: 0,
      offeringsIds: [],
      offeringsPrices: [],
      rcKeyPresent: !!(process.env.REACT_APP_REVENUECAT_API_KEY_IOS),
      elapsedMs: 0,
    };
    const nextDiag = { ...baseDiag };
    const flush = () => {
      nextDiag.elapsedMs = Date.now() - startTs;
      setDiag({ ...nextDiag });
    };
    flush();
    try {
      // 1. Leggi stato promo da backend (single source of truth) con timeout 5s.
      const withTimeout = (p, ms, label) => Promise.race([
        p,
        new Promise((_, rej) => setTimeout(() => rej(new Error(`timeout ${label} ${ms}ms`)), ms)),
      ]);
      try {
        const res = await withTimeout(api.get("/app/info"), 5000, "/app/info");
        const data = res?.data;
        nextDiag.appInfo = data;
        flush();
        if (data?.in_free_promo) {
          setPromoInfo({ inPromo: true, end: data.free_promo_end });
          setLoading(false);
          return;
        }
      } catch (e) {
        nextDiag.appInfoError = e?.message || String(e);
        flush();
        // Se /app/info fallisce procediamo comunque col paywall
      }
      // 2. Configura RevenueCat con user_id Pronto (idempotente) con timeout 5s.
      try {
        const ok = await withTimeout(iapConfigure(user?.user_id), 5000, "iapConfigure");
        nextDiag.configureOk = ok;
        flush();
      } catch (e) {
        nextDiag.configureError = e?.message || String(e);
        flush();
      }
      // 3. Fetcha i prodotti con timeout di 8s (se Apple StoreKit non risponde
      // — es. prodotti su App Store in stato "Metadati mancanti" — manteniamo
      // i fallback statici già visibili e mostriamo un avviso non-bloccante).
      // iter v31 r8 — Passiamo forceRefresh=true al primo mount per evitare
      // che RevenueCat/StoreKit servano prezzi cached in valuta sbagliata
      // (es. $ invece di €). Vedi nota in lib/iap.js → iapGetOfferings.
      let result;
      try {
        result = await withTimeout(iapGetOfferings({ forceRefresh: true }), 8000, "iapGetOfferings");
      } catch (e) {
        console.warn("[paywall] getOfferings timeout/error:", e);
        nextDiag.offeringsError = e?.message || String(e);
        setError("App Store ancora in sincronizzazione. I prezzi mostrati sono indicativi — riprova fra qualche minuto per completare l'acquisto.");
        flush();
        return;
      }
      if (!result || !result.packages || result.packages.length === 0) {
        nextDiag.offeringsCount = 0;
        nextDiag.offeringsError = result === null ? "result=null (iOS only / non configurato)" : "packages array vuoto";
        setError("App Store sta sincronizzando i prodotti. I prezzi mostrati sono indicativi — riprova fra qualche minuto.");
        flush();
        return;
      }
      nextDiag.offeringsCount = result.packages.length;
      nextDiag.offeringsIds = result.packages.map((p) => p.productIdentifier);
      // iter v31 r8 — Salviamo currencyCode + priceString per ogni package, così
      // se ricapita il bug "prezzi in $" basta aprire il pannello debug
      // (5 tap sul titolo) per verificarne la valuta servita da Apple StoreKit.
      nextDiag.offeringsPrices = result.packages.map((p) =>
        `${p.productIdentifier}: ${p.priceString} (${p.currencyCode || "?"})`
      );
      // Ordina: monthly → yearly, per plan
      const sorted = [...result.packages].sort((a, b) => {
        const order = (p) => {
          const period = p.subscriptionPeriod === "P1Y" ? 1 : 0;
          const plan = (p.productIdentifier || "").includes("pro") ? 1 : 0;
          return plan * 10 + period;
        };
        return order(a) - order(b);
      });
      setPackages(sorted);
      flush();
    } catch (e) {
      console.error("[paywall] load offerings:", e);
      nextDiag.offeringsError = e?.message || String(e);
      setError("Errore caricamento prodotti. Riprova.");
      flush();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  useEffect(() => {
    loadOfferings();
  }, [loadOfferings]);

  const handlePurchase = async (pkg) => {
    nativeHaptic("medium").catch(() => {});
    setPurchasingId(pkg.productIdentifier);
    try {
      const res = await iapPurchase(pkg);
      if (res.cancelled) {
        toast.info("Acquisto annullato.");
        return;
      }
      if (!res.ok) {
        toast.error(res.error || "Acquisto non riuscito.");
        return;
      }
      // Sync immediato al backend per UX istantanea (webhook RC arriva dopo)
      await iapSyncToBackend(res.entitlement);
      toast.success("Abbonamento attivato. Bentornato sul piano completo.");
      onPurchased?.(res.entitlement);
    } catch (e) {
      toast.error(e?.message || "Errore inatteso durante l'acquisto.");
    } finally {
      setPurchasingId(null);
    }
  };

  const handleRestore = async () => {
    nativeHaptic("light").catch(() => {});
    setRestoring(true);
    try {
      const res = await iapRestorePurchases();
      if (!res.ok) {
        toast.error(res.error || "Ripristino non riuscito.");
        return;
      }
      if (!res.hasActive) {
        toast.info("Nessun abbonamento attivo trovato su questo Apple ID.");
        return;
      }
      await iapSyncToBackend(res.entitlement);
      toast.success("Abbonamento ripristinato.");
      onPurchased?.(res.entitlement);
    } finally {
      setRestoring(false);
    }
  };

  const handleManage = async () => {
    await iapShowManageSubscription();
  };

  // ──────────────────────────────────────────────────────────────────
  // iter v30 r147 — Loading spinner rimosso: ora i prezzi statici sono visibili
  // subito (vedi STATIC_FALLBACK_PACKAGES). Mostriamo solo un avviso discreto
  // in cima quando ancora aspettiamo i prezzi reali da Apple.

  // ─── FREE PROMO MODE ─────────────────────────────────────────────
  // Pronto è gratis per tutti. Su iOS, anziché
  // mostrare i prezzi (e farsi addebitare il 15% Apple inutilmente),
  // mostriamo un messaggio rassicurante con la data di fine promo.
  if (promoInfo.inPromo) {
    const endDateLabel = (() => {
      if (!promoInfo.end) return "novembre 2026";
      try {
        const d = new Date(promoInfo.end);
        const months = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
        // Mostriamo "30 settembre 2026" (giorno prima di FREE_PROMO_END)
        d.setUTCDate(d.getUTCDate() - 1);
        return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
      } catch {
        return "novembre 2026";
      }
    })();
    return (
      <div className="space-y-6 px-4 py-10 max-w-md mx-auto text-center" data-testid="iap-paywall-promo">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#7C8B6E]/10 flex items-center justify-center">
          <Gift className="h-7 w-7 text-[#7C8B6E]" />
        </div>
        <div className="space-y-3">
          <h1
            onClick={onTitleTap}
            className="font-editorial text-3xl text-[#1C1C1A] cursor-pointer select-none"
            data-testid="iap-paywall-promo-title"
          >
            Pronto è gratis
          </h1>
          <p className="text-base text-neutral-700">
            Fino al <span className="font-semibold">{endDateLabel}</span> tutte le funzioni di Pronto sono incluse senza abbonamento.
          </p>
          <p className="text-sm text-neutral-600">
            Ci risentiamo qui per attivare il piano quando la promo finirà. Nessuna carta da inserire ora, nessun addebito a sorpresa.
          </p>
        </div>

        <div className="bg-[#F5F0E6] rounded-2xl p-4 text-left space-y-2 mt-6">
          <p className="text-xs uppercase tracking-wider text-[#7C8B6E] font-semibold">Cosa hai incluso adesso</p>
          <ul className="space-y-1.5">
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <CheckCircle2 className="h-4 w-4 text-[#7C8B6E] mt-0.5 flex-shrink-0" />
              <span>60 crediti smart al mese (foto · sopralluogo · social)</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <CheckCircle2 className="h-4 w-4 text-[#7C8B6E] mt-0.5 flex-shrink-0" />
              <span>5 immobili attivi</span>
            </li>
            <li className="flex items-start gap-2 text-sm text-neutral-700">
              <CheckCircle2 className="h-4 w-4 text-[#7C8B6E] mt-0.5 flex-shrink-0" />
              <span>Sopralluogo vocale · CRM · WhatsApp</span>
            </li>
          </ul>
        </div>

        <Button
          type="button"
          className="w-full bg-[#1C1C1A] hover:bg-black text-white mt-4"
          onClick={() => onPurchased?.(null)}
          data-testid="iap-promo-continue-btn"
        >
          Continua gratis
        </Button>

        {/* Restore resta visibile anche durante promo (policy Apple) */}
        <button
          type="button"
          onClick={handleRestore}
          disabled={restoring}
          className="inline-flex items-center gap-2 text-sm text-neutral-500 underline disabled:opacity-50"
          data-testid="iap-restore-btn"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          {restoring ? "Ripristino…" : "Ripristina acquisti"}
        </button>

        <PaywallDebugPanel show={showDebug} user={user} diag={diag} />
      </div>
    );
  }

  if (error || packages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4 text-center px-6" data-testid="iap-paywall-empty">
        <h2
          onClick={onTitleTap}
          className="text-sm font-semibold text-neutral-700 cursor-pointer select-none"
        >
          Paywall
        </h2>
        <p className="text-sm text-neutral-700">
          {error || "Nessun piano disponibile su questo dispositivo."}
        </p>
        <Button variant="outline" onClick={loadOfferings} data-testid="iap-retry-btn">
          Riprova
        </Button>
        <button
          type="button"
          onClick={handleRestore}
          disabled={restoring}
          className="text-sm underline text-neutral-500"
          data-testid="iap-restore-btn-empty"
        >
          {restoring ? "Ripristino…" : "Ripristina acquisti"}
        </button>
        <PaywallDebugPanel show={showDebug} user={user} diag={diag} />
      </div>
    );
  }

  return (
    <div className="space-y-6 px-4 py-6 max-w-md mx-auto" data-testid="iap-paywall">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1
          onClick={onTitleTap}
          className="font-editorial text-3xl text-[#1C1C1A] cursor-pointer select-none"
          data-testid="iap-paywall-title"
        >
          Sblocca tutto Pronto
        </h1>
        <p className="text-sm text-neutral-600">
          Cancellabile in qualunque momento dalle Impostazioni iOS.
        </p>
      </div>

      {/* iter v31 r1 — Toggle GLOBALE Mensile/Annuale (allineato sito).
          Applicato sia a Start che a Pro. */}
      <div className="grid grid-cols-2 gap-1 bg-[#F4F1EA] rounded-xl p-1 max-w-xs mx-auto" data-testid="iap-billing-toggle">
        <button
          type="button"
          onClick={() => setBillingPeriod("monthly")}
          className={`text-sm py-2 px-3 rounded-lg font-medium transition ${billingPeriod === "monthly" ? "bg-white text-[#1C1C1A] shadow-sm" : "text-neutral-500"}`}
          data-testid="iap-billing-monthly"
        >
          Mensile
        </button>
        <button
          type="button"
          onClick={() => setBillingPeriod("yearly")}
          className={`text-sm py-2 px-3 rounded-lg font-medium transition ${billingPeriod === "yearly" ? "bg-white text-[#1C1C1A] shadow-sm" : "text-neutral-500"}`}
          data-testid="iap-billing-yearly"
        >
          Annuale <span className="text-[#7C8B6E]">fino a -32%</span>
        </button>
      </div>

      {/* iter v31 r1 — 2 card (Start, Pro), entrambe rispondono al toggle globale.
          Start mensile €29,99 / annuale €249,99 · Pro mensile €49,99 / annuale €399,99. */}
      {(() => {
        const startMonthlyPkg = packages.find((p) => p.productIdentifier === "pronto_start_monthly");
        const startYearlyPkg  = packages.find((p) => p.productIdentifier === "pronto_start_yearly");
        const proMonthlyPkg   = packages.find((p) => p.productIdentifier === "pronto_pro_monthly");
        const proYearlyPkg    = packages.find((p) => p.productIdentifier === "pronto_pro_yearly");
        const startPkg = billingPeriod === "yearly" ? (startYearlyPkg || startMonthlyPkg) : startMonthlyPkg;
        const proPkg   = billingPeriod === "yearly" ? (proYearlyPkg   || proMonthlyPkg)   : proMonthlyPkg;

        const renderCard = (pkg, planKey, options = {}) => {
          if (!pkg) return null;
          const features = PLAN_FEATURES[planKey] || [];
          const planName = planKey === "pro" ? "Pronto Pro" : "Pronto Start";
          const featured = options.featured || false;
          const period = periodLabel(pkg.subscriptionPeriod);
          const isYearly = pkg.subscriptionPeriod === "P1Y";
          return (
            <Card
              key={pkg.identifier}
              className={`border ${featured ? "border-[#7C8B6E] ring-1 ring-[#7C8B6E]/40" : "border-neutral-200"}`}
              data-testid={`iap-package-${pkg.productIdentifier}`}
            >
              <CardContent className="p-5 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-neutral-500">
                      {planName}
                    </p>
                    <p className="text-2xl font-semibold text-[#1C1C1A] mt-1">
                      {pkg.priceString} <span className="text-sm font-normal text-neutral-500">{period}</span>
                    </p>
                    {isYearly && planKey === "base" && (
                      <p className="text-xs text-[#7C8B6E] mt-1">3 mesi in regalo · risparmi €99</p>
                    )}
                    {isYearly && planKey === "pro" && (
                      <p className="text-xs text-[#7C8B6E] mt-1">3 mesi in regalo · risparmi €189</p>
                    )}
                  </div>
                  {featured && (
                    <span className="text-[10px] uppercase tracking-wider bg-[#7C8B6E] text-white px-2 py-1 rounded-full">
                      Consigliato
                    </span>
                  )}
                </div>

                <ul className="space-y-1.5">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                      <CheckCircle2 className="h-4 w-4 text-[#7C8B6E] mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  type="button"
                  className={`w-full ${featured ? "bg-[#7C8B6E] hover:bg-[#6E7B62]" : "bg-[#1C1C1A] hover:bg-black"} text-white`}
                  disabled={purchasingId !== null || pkg.isFallback}
                  onClick={() => handlePurchase(pkg)}
                  data-testid={`iap-buy-${pkg.productIdentifier}`}
                >
                  {purchasingId === pkg.productIdentifier ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Acquisto in corso…
                    </span>
                  ) : pkg.isFallback ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Sincronizzazione…
                    </span>
                  ) : (
                    `Attiva ${planName}`
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        };

        return (
          <div className="space-y-3">
            {renderCard(startPkg, "base")}
            {renderCard(proPkg, "pro", { featured: true })}
          </div>
        );
      })()}

      {/* Note legali e azioni secondarie */}
      <div className="space-y-3 text-center pt-2">
        <p className="text-[11px] text-neutral-500 leading-relaxed px-2">
          L&apos;abbonamento si rinnova automaticamente al prezzo indicato salvo cancellazione almeno 24 ore prima della fine del periodo. Puoi gestirlo dalle Impostazioni del tuo Apple ID.
        </p>

        <div className="flex flex-col gap-2 items-center">
          <button
            type="button"
            onClick={handleRestore}
            disabled={restoring}
            className="inline-flex items-center gap-2 text-sm text-neutral-700 underline disabled:opacity-50"
            data-testid="iap-restore-btn"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            {restoring ? "Ripristino…" : "Ripristina acquisti"}
          </button>
          <button
            type="button"
            onClick={handleManage}
            className="inline-flex items-center gap-2 text-sm text-neutral-500 underline"
            data-testid="iap-manage-btn"
          >
            <Settings2 className="h-3.5 w-3.5" />
            Gestisci abbonamento
          </button>
        </div>

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-neutral-500 pt-1">
          <Shield className="h-3 w-3" />
          <span>Pagamento sicuro tramite Apple</span>
        </div>

        <div className="flex items-center justify-center gap-3 text-[11px] text-neutral-500 pt-1">
          <a href="https://prontoimmo.it/terms" className="underline" target="_blank" rel="noopener noreferrer">
            Termini
          </a>
          <span>·</span>
          <a href="https://prontoimmo.it/privacy" className="underline" target="_blank" rel="noopener noreferrer">
            Privacy
          </a>
        </div>
      </div>

      {/* iter v31 r2 — Pannello diagnostico (5 tap sul titolo o ?dbg=1 in URL). */}
      <PaywallDebugPanel show={showDebug} user={user} diag={diag} />
    </div>
  );
}
